import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { setSex as updateSex } from "../../redux/userSlice";
import { MinorUserContext } from "../../context/UserNameProvider";
import Question from "../../components/Question";
import { Box, Collapse, FormControl, FormControlLabel, Checkbox, useTheme, Typography } from "@mui/material";
import ContinueButton from "../../components/ContinueButton";
import MoveButton from "../../components/MoveButton";
import { checkboxSx, continueButtonSx } from "../../lib/uiformatting";
import setPageAndDeferNavigate from "../../lib/setPageAndDeferNavigate";

export default function Menstrual() {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const theme = useTheme();

    const { isMinor, firstName } = useContext(MinorUserContext);

    const user = useSelector((state) => state.user);

    const options = {
        "Regular (predictable, every 21-35 days)":
            "Regular (predictable, every 21-35 days)",
        "Irregular (unpredictable, <21 or >35 days)":
            "Irregular (unpredictable, <21 or >35 days)",
        "I don't have periods": "I don't have periods",
    };

    const additionalOptions = {
        "Do you have a hormonal IUD (Mirena, Kyleena, etc.)?": "iud",
        "Had uterine surgery (e.g., hysterectomy, ablation)?": "surgery",
    };

    const [cycle, setCycle] = useState(user.sex?.cycle || "");
    const [additional, setAdditional] = useState([]);

    useEffect(() => {
        const storedAdditional = user.sex?.additional;
        if (storedAdditional) {
            const arr = [];
            if (storedAdditional.iud === "Y") arr.push("iud");
            if (storedAdditional.surgery === "Y") arr.push("surgery");
            setAdditional(arr);
        }
    }, [user.sex?.additional]);

    const handleChange = (value) => {
        setCycle(value);
        if (value !== "I don't have periods") {
            setAdditional([]);
        }
    };

    const handleAdditionalChange = (e) => {
        const { name, checked } = e.target;
        if (checked) {
            setAdditional((prev) => [...prev, name]);
        } else {
            setAdditional((prev) => prev.filter((item) => item !== name));
        }
    };

    const handleNext = () => {
        if (!cycle) return;
        if (cycle === "I don't have periods") {
            const updatedAdditional = {
                iud: additional.includes("iud") ? "Y" : "N",
                surgery: additional.includes("surgery") ? "Y" : "N",
            };
            dispatch(updateSex({ ...user.sex, cycle, additional: updatedAdditional }));
        } else {
            dispatch(updateSex({ ...user.sex, cycle, additional: [] }));
        }
        setPageAndDeferNavigate(dispatch, '/contraceptive', () =>
            navigate('/contraceptive', { state: { returning: false } })
        );
    };

    const handleBack = () => {
        setPageAndDeferNavigate(dispatch, '/sex-at-birth', navigate);
    };

    return (
        <Question
            onBack={handleBack}
            title={`How would you describe ${isMinor ? `${firstName}'s` : "your"} menstrual cycle?`}
            subtitle="Select one"
            input={
                <Box
                    sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            justifyContent: "center",
                            width: "100%",
                            maxWidth: "350px",
                            gap: "1rem",
                            mt: 2,
                        }}
                    >
                        {Object.keys(options).map((label) => (
                            <MoveButton
                                sx={{ textAlign: "left", pl: 3 }}
                                key={options[label]}
                                onClick={() => handleChange(options[label])}
                                title={label}
                                selected={cycle === options[label]}
                            />
                        ))}
                    </Box>

                    <Collapse
                        in={cycle === "I don't have periods"}
                        mountOnEnter
                        unmountOnExit
                        sx={{ width: "100%", maxWidth: "350px", mt: "2rem", ml: "1rem", mr: "1rem" }}
                    >
                        <Typography
                            variant="subtitle1"
                            sx={{
                                color: theme.palette.text.secondary,
                                mb: 2,
                                ml: 2,
                                fontStyle: "italic",
                                textAlign: "left",
                            }}
                        >
                            Please add more details
                        </Typography>
                        <FormControl
                            sx={{ display: "flex", flexDirection: "column", gap: "1rem" }}
                        >
                            {Object.keys(additionalOptions).map((key) => (
                                <FormControlLabel
                                    key={key}
                                    control={
                                        <Checkbox
                                            checked={additional.includes(additionalOptions[key])}
                                            onChange={handleAdditionalChange}
                                            name={additionalOptions[key]}
                                        />
                                    }
                                    label={key}
                                    sx={{ ...checkboxSx }}
                                />
                            ))}
                        </FormControl>
                    </Collapse>
                </Box>
            }
            footer={
                <Box
                    sx={{
                        ...continueButtonSx,
                        width: "100%",
                        maxWidth: "900px",
                        margin: "0 auto",
                        position: "sticky",
                        bottom: 0,
                    }}
                >
                    <ContinueButton
                        onClick={handleNext}
                        title="Continue"
                        selected={cycle !== ""}
                        disabled={cycle === ""}
                    />
                </Box>
            }
        />
    );
}
